"use client";

import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Box, Typography } from "@mui/material";
import { Icon } from "@/components/icons";
import { APP_MAX_WIDTH } from "@/components/PageContent/PageContent";
import { Z_INDEX } from "@/constants/zIndex";
import {
  TEMP_TEXT_SEMIBOLD_L,
  TEMP_TEXT_SEMIBOLD_XL,
  TEMP_TITLE_BOLD_M,
  TEMP_TEXT_MEDIUM_S,
} from "@/constants/tempTypo";
import { useAppLanguage, useTranslator } from "@/lang";

interface ConsentBottomSheetProps {
  open: boolean;
  onAgree: (marketingAgreed: boolean) => void;
  onBack: () => void;
}

type ConsentKey = "terms" | "privacy" | "youth" | "marketing";

interface ConsentItem {
  key: ConsentKey;
  labelKey: string;
  required: boolean;
  content: string;
  contentJa: string;
  contentEn: string;
  contentZhTw: string;
}

const TERMS_CONTENT = `제1조(목적)
이 약관은 (주)키우링 회사(전자상거래 사업자)가 운영하는 뮤즈링 사이버 몰(이하 "몰"이라 한다)에서 제공하는 인터넷 관련 서비스(이하 "서비스"라 한다)를 이용함에 있어 사이버 몰과 이용자의 권리·의무 및 책임사항을 규정함을 목적으로 합니다.

제2조(정의)
① "몰"이란 (주)키우링 회사가 재화 또는 용역을 이용자에게 제공하기 위하여 컴퓨터 등 정보통신설비를 이용하여 거래할 수 있도록 설정한 가상의 영업장을 말합니다.
② "이용자"란 "몰"에 접속하여 이 약관에 따라 "몰"이 제공하는 서비스를 받는 회원 및 비회원을 말합니다.
③ "회원"이라 함은 "몰"에 회원등록을 한 자로서, 계속적으로 "몰"이 제공하는 서비스를 이용할 수 있는 자를 말합니다.

제3조(약관의 명시와 개정)
"몰"은 이 약관의 내용과 상호, 영업소 소재지, 연락처 등을 이용자가 쉽게 알 수 있도록 게시합니다. 약관 개정 시 적용일자 7일 이전부터 공지하며, 이용자에게 불리한 변경의 경우 최소 30일 전 공지합니다.

제4조(서비스의 제공)
"몰"은 재화·용역 정보 제공, 구매계약 체결, 콘텐츠 제공 등의 업무를 수행합니다. 서비스 내용은 사정에 따라 변경될 수 있으며 변경 시 즉시 공지합니다.

제5조(회원가입 및 탈퇴)
회원은 가입 양식에 따라 정보를 기입하고 약관에 동의함으로써 회원가입을 신청합니다. 회원은 언제든지 탈퇴를 요청할 수 있으며, "몰"은 즉시 탈퇴를 처리합니다.

제6조(개인정보보호)
"몰"은 서비스 제공에 필요한 최소한의 개인정보만을 수집하며, 수집된 개인정보는 목적 외 용도로 이용하지 않습니다. 자세한 내용은 개인정보 처리방침을 따릅니다.

제7조(이용자의 의무)
이용자는 허위 정보 등록, 타인 정보 도용, 저작권 침해, 공서양속에 반하는 행위를 해서는 안 됩니다.

제8조(분쟁해결 및 준거법)
"몰"과 이용자 간 분쟁은 한국법을 적용하며, 이용자의 주소지를 관할하는 지방법원에 제기합니다.

전체 약관은 [마이페이지 > 약관 및 정책 > 이용약관]에서 언제든지 확인하실 수 있습니다.`;

const PRIVACY_CONTENT = `"주식회사 키우링"(이하 "회사")는 관련 법령에 의거한 개인정보처리방침을 정하여 이용자 권익 보호에 최선을 다합니다.

1. 처리 목적
고객 가입의사 확인, 본인 식별·인증, 회원자격 유지·관리, 이벤트 참여, 맞춤 콘텐츠 제공

2. 수집 항목
이메일, 이름(닉네임), 소셜 식별 정보, 연령, 성별

3. 보유 및 이용 기간
- 마지막 접속일 기준 5년간 저장
- 회원 탈퇴 후 1년간 저장

4. 제3자 제공
회사는 이용자의 개인정보를 원칙적으로 외부에 공개하지 않습니다. 다만, 법령의 규정에 의거하거나 이용자가 사전에 동의한 경우는 예외로 합니다.

5. 정보주체의 권리
이용자는 언제든지 개인정보 열람, 정정, 삭제, 처리 정지를 요구할 수 있으며, 회사는 지체 없이 조치합니다.

6. 안전성 확보 조치
회사는 개인정보 암호화 통신, 중요 정보 암호화 보관, 접속 통제, 접근 권한 제한 등의 보안 조치를 운영합니다.

7. 개인정보 보호책임자
· 성명: 김태성
· 이메일: contact@museling.me

전체 처리방침은 [마이페이지 > 약관 및 정책 > 개인정보 처리방침]에서 확인하실 수 있습니다.`;

const YOUTH_CONTENT = `주식회사 키우링(이하 "회사")은 청소년을 보호하고 안전한 플랫폼 환경을 조성하기 위해 아래와 같은 정책을 운영합니다.

1. 이용 연령 제한
- 본 서비스는 만 17세 이상 사용자만 이용 가능합니다.
- 회원가입 시 본인의 연령이 기준을 충족함을 확인해야 합니다.
- 허위 정보로 가입할 경우 이용이 제한될 수 있습니다.

2. 콘텐츠 제공 원칙
- AI 생성 콘텐츠는 성인 사용자에게만 제공됩니다.
- 청소년이 접근할 수 없도록 기술적·정책적 보호 조치를 시행합니다.

3. 금지 콘텐츠
- 미성년자가 등장하거나 연상되는 콘텐츠
- 아동·청소년 대상 성적 표현 또는 착취 콘텐츠
- 불법, 유해, 폭력적 콘텐츠

4. 신고 및 관리
회사는 자동 필터링 및 모니터링을 통해 부적절한 콘텐츠를 검토·삭제하며, 신고는 contact@museling.me으로 접수받습니다.

5. 청소년 보호 책임자
· 성명: 김태성
· 이메일: contact@museling.me

전체 정책은 [마이페이지 > 약관 및 정책 > 청소년 보호 정책]에서 확인하실 수 있습니다.`;

const MARKETING_CONTENT = `(주)키우링은 회원님께 더 풍부한 뮤즈링 경험과 새로운 소식을 전해드리기 위해 아래와 같이 마케팅 정보를 수집·이용합니다.

수집 항목
이메일, 이름(닉네임), 소셜 식별 정보, 연령, 성별

이용 목적
- 신규 캐릭터·스토리 출시 및 업데이트 안내
- 이벤트, 프로모션, 할인 혜택 안내
- 맞춤형 추천 콘텐츠 제공
- 회원 대상 설문·리워드 안내

보유 및 이용 기간
회원 탈퇴 또는 동의 철회 시까지

본 동의는 선택 사항이며, 거부하셔도 회원가입 및 기본 서비스 이용에는 제한이 없습니다. 동의 후에도 마이페이지 설정에서 언제든 철회하실 수 있습니다.`;

// 일본어 요약 약관 — PG(KGイニシス) 심사 요건상 일본 이용자 대상 표현으로 별도 작성.
// 한국 법령 명칭 인용 금지, 준거법은 일본법. (참고: NavigationStack/routes/TermsRoute.tsx)
const TERMS_CONTENT_JA = `第1条(目的)
本規約は、Kiuring Inc.(株式会社キウリング、以下「当社」といいます)が運営するオンラインサービス「MuseLing(ミューズリング)」(以下「本サービス」といいます)の利用に関し、当社と利用者との間の権利・義務及び責任事項を定めることを目的とします。

第2条(定義)
① 「本サービス」とは、当社が情報通信設備を利用して、AIキャラクターとのチャット等のデジタルコンテンツを利用者に提供するサービスをいいます。
② 「利用者」とは、本サービスにアクセスし、本規約に従って本サービスの提供を受ける会員及び非会員をいいます。
③ 「会員」とは、本サービスに会員登録を行った者であって、継続的に本サービスを利用できる者をいいます。

第3条(規約の明示と改定)
当社は、本規約の内容、商号、所在地、連絡先等を利用者が容易に確認できるよう掲示します。規約を改定する場合は適用日の7日前までに告知し、利用者に不利な変更の場合は最低30日前に告知します。

第4条(サービスの提供)
当社は、コンテンツに関する情報の提供、利用契約の締結、コンテンツの提供等の業務を行います。サービスの内容は事情により変更されることがあり、変更時は速やかに告知します。

第5条(会員登録及び退会)
会員は、登録フォームに情報を記入し、本規約に同意することにより会員登録を申請します。会員はいつでも退会を申し出ることができ、当社は速やかに退会処理を行います。

第6条(個人情報保護)
当社は、サービス提供に必要な最小限の個人情報のみを収集し、収集した個人情報を目的外の用途に利用しません。詳細は別途定めるプライバシーポリシーによります。

第7条(利用者の義務)
利用者は、虚偽情報の登録、他人の情報の盗用、著作権の侵害、公序良俗に反する行為を行ってはなりません。

第8条(紛争解決及び準拠法)
本規約は日本法に準拠します。本サービスに関して紛争が生じた場合は、日本の関係法令及び商慣習に従い、誠実に協議のうえ解決を図ります。

全文は[マイページ > 規約とポリシー > 利用規約]からいつでもご確認いただけます。`;

const PRIVACY_CONTENT_JA = `Kiuring Inc.(株式会社キウリング、以下「当社」)は、関係法令に基づきプライバシーポリシーを定め、利用者の権利保護に最善を尽くします。

1. 利用目的
登録意思の確認、本人識別・認証、会員資格の維持・管理、イベントへの参加、パーソナライズされたコンテンツの提供

2. 収集項目
メールアドレス、氏名(ニックネーム)、ソーシャル識別情報、年齢、性別

3. 保有及び利用期間
- 最終アクセス日から5年間保存
- 退会後1年間保存

4. 第三者提供
当社は、利用者の個人情報を原則として外部に提供しません。ただし、法令の規定に基づく場合、又は利用者が事前に同意した場合はこの限りではありません。

5. 利用者の権利
利用者はいつでも、自己の個人情報の閲覧、訂正、削除、処理停止を請求することができ、当社は遅滞なく対応します。

6. 安全管理措置
当社は、通信の暗号化、重要情報の暗号化保管、アクセス制御、アクセス権限の制限等のセキュリティ対策を実施しています。

7. 個人情報保護責任者
· 氏名: キム・テソン
· メールアドレス: contact@museling.me

全文は[マイページ > 規約とポリシー > プライバシーポリシー]からご確認いただけます。`;

// 年齢確認ポリシー — 일본판은 '연령 확인(17세 이상 이용 확인)' 취지로 작성.
const YOUTH_CONTENT_JA = `Kiuring Inc.(株式会社キウリング、以下「当社」)は、安全なプラットフォーム環境を提供するため、以下のとおり年齢確認ポリシーを運用しています。

1. 利用年齢の制限
- 本サービスは、17歳以上の方のみご利用いただけます。
- 会員登録の際、ご自身の年齢が基準を満たしていることをご確認いただきます。
- 虚偽の情報で登録した場合、利用が制限されることがあります。

2. コンテンツ提供の原則
- 成人向けのAI生成コンテンツは、成人の利用者にのみ提供されます。
- 基準年齢未満の方がアクセスできないよう、技術的・ポリシー上の保護措置を実施しています。

3. 禁止コンテンツ
- 未成年者が登場し、又は連想されるコンテンツ
- 未成年者を対象とする性的表現又は搾取的なコンテンツ
- 違法、有害、暴力的なコンテンツ

4. 通報及び管理
当社は、自動フィルタリング及びモニタリングにより不適切なコンテンツを審査・削除しており、通報は contact@museling.me にて受け付けています。

5. 担当責任者
· 氏名: キム・テソン
· メールアドレス: contact@museling.me

全文は[マイページ > 規約とポリシー]からご確認いただけます。`;

const MARKETING_CONTENT_JA = `Kiuring Inc.(株式会社キウリング)は、より充実したミューズリング体験と最新情報をお届けするため、以下のとおりマーケティング情報を収集・利用します。

収集項目
メールアドレス、氏名(ニックネーム)、ソーシャル識別情報、年齢、性別

利用目的
- 新キャラクター・ストーリーのリリース及びアップデートのお知らせ
- イベント、プロモーション、割引特典のお知らせ
- パーソナライズされたおすすめコンテンツの提供
- 会員向けアンケート・リワードのお知らせ

保有及び利用期間
退会又は同意の撤回まで

本同意は任意であり、同意されなくても会員登録及び基本サービスの利用に制限はありません。同意後も、マイページの設定からいつでも撤回できます。`;

// 영어 요약 약관 — 준거법은 대한민국법 유지(법적 페이지와 동일 정책), 자연스러운 앱 카피 톤.
const TERMS_CONTENT_EN = `Article 1 (Purpose)
These Terms govern the rights, obligations, and responsibilities between Kiuring Inc. (the e-commerce operator, the "Company") and users of the internet services (the "Services") provided through the MuseLing cyber mall (the "Mall").

Article 2 (Definitions)
1. "Mall" means the virtual place of business set up by Kiuring Inc. using information and communication facilities such as computers so that goods or services can be provided to users.
2. "User" means any member or non-member who accesses the Mall and receives the Services under these Terms.
3. "Member" means a person who has registered as a member of the Mall and may continuously use the Services provided by the Mall.

Article 3 (Posting and Amendment of the Terms)
The Mall posts these Terms, its trade name, business address, and contact information so that users can easily find them. When amending the Terms, the Mall gives notice at least 7 days before the effective date, and at least 30 days in advance for changes unfavorable to users.

Article 4 (Provision of Services)
The Mall provides information on goods and services, concludes purchase contracts, and delivers content. The Services may change depending on circumstances, and any change is announced immediately.

Article 5 (Membership and Withdrawal)
A user applies for membership by filling in the registration form and agreeing to these Terms. Members may request withdrawal at any time, and the Mall processes it immediately.

Article 6 (Privacy Protection)
The Mall collects only the minimum personal information needed to provide the Services and does not use it for any other purpose. Details follow the Privacy Policy.

Article 7 (User Obligations)
Users must not register false information, misuse another person's information, infringe copyrights, or act against public order and good morals.

Article 8 (Dispute Resolution and Governing Law)
Disputes between the Mall and users are governed by the laws of the Republic of Korea, and lawsuits are filed with the district court having jurisdiction over the user's address.

You can review the full Terms anytime at [My Page > Terms & Policies > Terms of Service].`;

const PRIVACY_CONTENT_EN = `Kiuring Inc. (the "Company") maintains a privacy policy in accordance with applicable laws and does its best to protect users' rights.

1. Purposes of Processing
Confirming intent to sign up, identification and authentication, maintaining and managing membership, event participation, providing personalized content

2. Information Collected
Email, name (nickname), social login identifiers, age, gender

3. Retention and Use Period
- Stored for 5 years from the last access date
- Stored for 1 year after account deletion

4. Provision to Third Parties
In principle, the Company does not disclose users' personal information to outside parties, except where required by law or with the user's prior consent.

5. Rights of Data Subjects
Users may request access to, correction of, deletion of, or suspension of processing of their personal information at any time, and the Company acts without delay.

6. Security Measures
The Company operates security measures such as encrypted communication, encrypted storage of important information, access control, and restriction of access rights.

7. Privacy Officer
- Name: Taesung Kim
- Email: contact@museling.me

You can review the full policy at [My Page > Terms & Policies > Privacy Policy].`;

const YOUTH_CONTENT_EN = `Kiuring Inc. (the "Company") operates the following policy to protect minors and maintain a safe platform environment.

1. Age Restriction
- This service is available only to users aged 17 or older.
- When signing up, you must confirm that you meet the age requirement.
- Registering with false information may restrict your use of the service.

2. Content Principles
- AI-generated adult content is provided to adult users only.
- Technical and policy safeguards prevent access by minors.

3. Prohibited Content
- Content featuring or suggesting minors
- Sexual expression or exploitative content involving minors
- Illegal, harmful, or violent content

4. Reporting and Moderation
The Company reviews and removes inappropriate content through automated filtering and monitoring. Reports are accepted at contact@museling.me.

5. Responsible Officer
- Name: Taesung Kim
- Email: contact@museling.me

You can review the full policy at [My Page > Terms & Policies].`;

const MARKETING_CONTENT_EN = `Kiuring Inc. collects and uses marketing information as follows to bring you a richer MuseLing experience and the latest news.

Information Collected
Email, name (nickname), social login identifiers, age, gender

Purposes of Use
- Announcing new characters, stories, and updates
- Announcing events, promotions, and discounts
- Providing personalized recommended content
- Surveys and reward notices for members

Retention and Use Period
Until account deletion or withdrawal of consent

This consent is optional, and declining does not limit sign-up or basic use of the service. You can withdraw consent anytime in My Page settings.`;

// 번체 중문 요약 약관 — 台灣 관용 표현, 준거법은 대한민국법 유지.
const TERMS_CONTENT_ZH_TW = `第1條(目的)
本條款旨在規範 Kiuring Inc.(電子商務經營者,以下稱「公司」)所營運的 MuseLing 網路商城(以下稱「商城」)所提供之網路相關服務(以下稱「服務」)的使用,以及商城與使用者之間的權利、義務與責任事項。

第2條(定義)
1. 「商城」指公司為向使用者提供商品或服務,利用電腦等資訊通訊設備所設置的虛擬營業場所。
2. 「使用者」指連線至商城並依本條款接受商城所提供服務的會員及非會員。
3. 「會員」指已在商城完成會員註冊,並可持續使用商城所提供服務之人。

第3條(條款之公告與修訂)
商城會將本條款內容、商號、營業所在地、聯絡方式等,以使用者容易知悉的方式公告。修訂條款時,將於適用日7日前公告;若為對使用者不利之變更,則至少於30日前公告。

第4條(服務之提供)
商城提供商品與服務資訊、締結購買契約、提供內容等業務。服務內容可能因情況變更,變更時將立即公告。

第5條(會員註冊與退出)
使用者依註冊表單填寫資料並同意本條款後即可申請註冊。會員得隨時申請退出,商城將立即處理。

第6條(個人資料保護)
商城僅蒐集提供服務所需之最少個人資料,且不會將所蒐集之個人資料用於目的外用途。詳情依個人資料處理方針辦理。

第7條(使用者義務)
使用者不得登錄虛假資訊、盜用他人資訊、侵害著作權,或從事違反公序良俗之行為。

第8條(爭議解決與準據法)
商城與使用者間之爭議適用大韓民國法律,訴訟由使用者住所地之管轄地方法院管轄。

完整條款可隨時於[我的頁面 > 條款與政策 > 服務條款]查看。`;

const PRIVACY_CONTENT_ZH_TW = `Kiuring Inc.(以下稱「公司」)依據相關法令制定個人資料處理方針,盡力保護使用者權益。

1. 處理目的
確認註冊意願、本人識別與驗證、會員資格維持與管理、活動參與、提供個人化內容

2. 蒐集項目
電子郵件、姓名(暱稱)、社群帳號識別資訊、年齡、性別

3. 保存及使用期間
- 自最後連線日起保存5年
- 退出會員後保存1年

4. 提供予第三方
公司原則上不會將使用者的個人資料提供給外部。但依法令規定或經使用者事先同意者,不在此限。

5. 資料主體之權利
使用者得隨時請求查閱、更正、刪除個人資料或停止處理,公司將立即處理。

6. 安全性確保措施
公司實施通訊加密、重要資訊加密保存、連線控管、存取權限限制等安全措施。

7. 個人資料保護負責人
- 姓名: Taesung Kim
- 電子郵件: contact@museling.me

完整方針可於[我的頁面 > 條款與政策 > 個人資料處理方針]查看。`;

const YOUTH_CONTENT_ZH_TW = `Kiuring Inc.(以下稱「公司」)為保護青少年並打造安全的平台環境,施行以下政策。

1. 使用年齡限制
- 本服務僅限年滿17歲之使用者使用。
- 註冊會員時,須確認本人年齡符合標準。
- 以虛假資訊註冊者,其使用可能受到限制。

2. 內容提供原則
- AI 生成之成人內容僅提供給成年使用者。
- 施行技術及政策上的保護措施,防止青少年接觸。

3. 禁止內容
- 出現未成年人或令人聯想到未成年人的內容
- 以兒童、青少年為對象的性表現或剝削內容
- 違法、有害、暴力內容

4. 檢舉與管理
公司透過自動過濾與監控審查並刪除不當內容,檢舉請寄至 contact@museling.me。

5. 青少年保護負責人
- 姓名: Taesung Kim
- 電子郵件: contact@museling.me

完整政策可於[我的頁面 > 條款與政策]查看。`;

const MARKETING_CONTENT_ZH_TW = `Kiuring Inc. 為向會員提供更豐富的 MuseLing 體驗與最新消息,依下列內容蒐集及使用行銷資訊。

蒐集項目
電子郵件、姓名(暱稱)、社群帳號識別資訊、年齡、性別

使用目的
- 新角色、新故事上線及更新通知
- 活動、促銷、優惠資訊通知
- 提供個人化推薦內容
- 會員問卷調查與獎勵通知

保存及使用期間
至退出會員或撤回同意為止

本同意為選填項目,即使不同意,也不影響註冊會員及使用基本服務。同意後亦可隨時於我的頁面設定中撤回。`;

const CONSENT_ITEMS: ConsentItem[] = [
  {
    key: "terms",
    labelKey: "consent.item_terms",
    required: true,
    content: TERMS_CONTENT,
    contentJa: TERMS_CONTENT_JA,
    contentEn: TERMS_CONTENT_EN,
    contentZhTw: TERMS_CONTENT_ZH_TW,
  },
  {
    key: "privacy",
    labelKey: "consent.item_privacy",
    required: true,
    content: PRIVACY_CONTENT,
    contentJa: PRIVACY_CONTENT_JA,
    contentEn: PRIVACY_CONTENT_EN,
    contentZhTw: PRIVACY_CONTENT_ZH_TW,
  },
  {
    key: "youth",
    labelKey: "consent.item_youth",
    required: true,
    content: YOUTH_CONTENT,
    contentJa: YOUTH_CONTENT_JA,
    contentEn: YOUTH_CONTENT_EN,
    contentZhTw: YOUTH_CONTENT_ZH_TW,
  },
  {
    key: "marketing",
    labelKey: "consent.item_marketing",
    required: false,
    content: MARKETING_CONTENT,
    contentJa: MARKETING_CONTENT_JA,
    contentEn: MARKETING_CONTENT_EN,
    contentZhTw: MARKETING_CONTENT_ZH_TW,
  },
];

const EASING: [number, number, number, number] = [0.25, 0.1, 0.25, 1];

const BACKDROP_Z = Z_INDEX.IMAGE_VIEWER + 20;
const SHEET_Z = BACKDROP_Z + 1;

export default function ConsentBottomSheet({
  open,
  onAgree,
  onBack,
}: ConsentBottomSheetProps) {
  const { t } = useTranslator();
  const [agreements, setAgreements] = useState<Record<ConsentKey, boolean>>({
    terms: false,
    privacy: false,
    youth: false,
    marketing: false,
  });
  const [expanded, setExpanded] = useState<Record<ConsentKey, boolean>>({
    terms: false,
    privacy: false,
    youth: false,
    marketing: false,
  });

  const allChecked = useMemo(
    () => CONSENT_ITEMS.every((it) => agreements[it.key]),
    [agreements],
  );
  const requiredAllChecked = useMemo(
    () =>
      CONSENT_ITEMS.filter((it) => it.required).every(
        (it) => agreements[it.key],
      ),
    [agreements],
  );

  const toggle = (key: ConsentKey) =>
    setAgreements((prev) => ({ ...prev, [key]: !prev[key] }));

  const toggleAll = () => {
    const next = !allChecked;
    setAgreements({
      terms: next,
      privacy: next,
      youth: next,
      marketing: next,
    });
  };

  const toggleExpanded = (key: ConsentKey) =>
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const handleConfirm = () => {
    if (!requiredAllChecked) return;
    onAgree(agreements.marketing);
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="consent-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25, ease: EASING }}
            onClick={onBack}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              zIndex: BACKDROP_Z,
            }}
          />

          <motion.div
            key="consent-sheet"
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "tween", duration: 0.32, ease: EASING }}
            style={{
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              maxWidth: APP_MAX_WIDTH,
              margin: "0 auto",
              maxHeight: "88vh",
              backgroundColor: "#1a1a1a",
              borderTopLeftRadius: 24,
              borderTopRightRadius: 24,
              zIndex: SHEET_Z,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              textShadow: "none",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                pt: "10px",
                pb: "4px",
                flexShrink: 0,
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 4,
                  borderRadius: "9999px",
                  backgroundColor: "#3a3a3a",
                }}
              />
            </Box>

            <Box sx={{ px: "24px", pt: "12px", pb: "16px", flexShrink: 0 }}>
              <Typography
                sx={{
                  ...TEMP_TITLE_BOLD_M,
                  color: "#ffffff",
                  lineHeight: 1.35,
                }}
              >
                {t('consent.title_1')}
              </Typography>
              <Typography
                sx={{
                  ...TEMP_TITLE_BOLD_M,
                  color: "#ffffff",
                  lineHeight: 1.35,
                }}
              >
                {t('consent.title_2')}
              </Typography>
              <Typography
                sx={{
                  ...TEMP_TEXT_MEDIUM_S,
                  color: "rgba(255, 255, 255, 0.5)",
                  mt: "8px",
                }}
              >
                {t('consent.age_note')}
              </Typography>
            </Box>

            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                overflowX: "hidden",
                px: "20px",
                pb: "12px",
                WebkitOverflowScrolling: "touch",
              }}
            >
              <AllAgreeRow checked={allChecked} onToggle={toggleAll} />

              <Box
                sx={{
                  height: 1,
                  backgroundColor: "rgba(255, 255, 255, 0.08)",
                  mx: "4px",
                  my: "6px",
                }}
              />

              {CONSENT_ITEMS.map((item) => (
                <ConsentRow
                  key={item.key}
                  item={item}
                  checked={agreements[item.key]}
                  expanded={expanded[item.key]}
                  onToggleCheck={() => toggle(item.key)}
                  onToggleExpand={() => toggleExpanded(item.key)}
                />
              ))}
            </Box>

            <Box
              sx={{
                px: "20px",
                pt: "12px",
                pb: "calc(20px + env(safe-area-inset-bottom, 0px))",
                flexShrink: 0,
                bgcolor: "#1a1a1a",
              }}
            >
              <Box
                onClick={requiredAllChecked ? handleConfirm : undefined}
                sx={{
                  height: 54,
                  borderRadius: "14px",
                  bgcolor: requiredAllChecked ? "#FFFFFF" : "#2a2a2a",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: requiredAllChecked ? "pointer" : "default",
                  transition: "background-color 0.18s",
                  WebkitTapHighlightColor: "transparent",
                  userSelect: "none",
                }}
              >
                <Typography
                  sx={{
                    ...TEMP_TEXT_SEMIBOLD_XL,
                    color: requiredAllChecked
                      ? "#4b00cc"
                      : "rgba(255, 255, 255, 0.35)",
                    textShadow: "none",
                  }}
                >
                  {t('consent.agree_and_start')}
                </Typography>
              </Box>
            </Box>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function AllAgreeRow({
  checked,
  onToggle,
}: {
  checked: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslator();
  return (
    <Box
      onClick={onToggle}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        px: "12px",
        py: "14px",
        borderRadius: "12px",
        bgcolor: checked
          ? "rgba(124, 58, 237, 0.18)"
          : "rgba(255, 255, 255, 0.04)",
        cursor: "pointer",
        transition: "background-color 0.18s",
        WebkitTapHighlightColor: "transparent",
        userSelect: "none",
      }}
    >
      <CheckCircle checked={checked} accent />
      <Typography
        sx={{
          ...TEMP_TEXT_SEMIBOLD_L,
          color: "#ffffff",
          fontWeight: 700,
          flex: 1,
        }}
      >
        {t('consent.agree_all')}
      </Typography>
    </Box>
  );
}

interface ConsentRowProps {
  item: ConsentItem;
  checked: boolean;
  expanded: boolean;
  onToggleCheck: () => void;
  onToggleExpand: () => void;
}

function ConsentRow({
  item,
  checked,
  expanded,
  onToggleCheck,
  onToggleExpand,
}: ConsentRowProps) {
  const { t } = useTranslator();
  const { language } = useAppLanguage();
  const content =
    language === "ja"
      ? item.contentJa
      : language === "en"
        ? item.contentEn
        : language === "zh_TW" || language === "zh"
          ? item.contentZhTw
          : item.content;
  return (
    <Box sx={{ borderRadius: "10px" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          px: "12px",
          py: "10px",
          WebkitTapHighlightColor: "transparent",
          userSelect: "none",
        }}
      >
        <Box
          onClick={onToggleCheck}
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            p: "4px",
            m: "-4px",
          }}
        >
          <CheckCircle checked={checked} />
        </Box>

        <Box
          onClick={onToggleExpand}
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
            py: "4px",
            minWidth: 0,
          }}
        >
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 500,
              lineHeight: 1.4,
              color: "rgba(255, 255, 255, 0.92)",
            }}
            noWrap
          >
            <Box
              component="span"
              sx={{
                color: item.required
                  ? "rgba(255, 255, 255, 0.5)"
                  : "rgba(255, 255, 255, 0.4)",
                fontWeight: 500,
                mr: "6px",
              }}
            >
              {item.required ? t('consent.required') : t('consent.optional')}
            </Box>
            {t(item.labelKey)}
          </Typography>

          <Box
            sx={{
              transition: "transform 0.22s ease",
              transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
              display: "flex",
              ml: "8px",
              flexShrink: 0,
            }}
          >
            <Icon
              name="icChevronDown"
              size={18}
              color="rgba(255, 255, 255, 0.4)"
            />
          </Box>
        </Box>
      </Box>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.24, ease: EASING }}
            style={{ overflow: "hidden" }}
          >
            <Box
              sx={{
                mx: "12px",
                mb: "10px",
                px: "14px",
                py: "12px",
                borderRadius: "10px",
                bgcolor: "rgba(255, 255, 255, 0.04)",
              }}
            >
              {renderContent(content)}
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
}

function CheckCircle({
  checked,
  accent = false,
}: {
  checked: boolean;
  accent?: boolean;
}) {
  if (checked) {
    return (
      <Box
        sx={{
          width: 22,
          height: 22,
          borderRadius: "50%",
          bgcolor: accent ? "#7c3aed" : "#7c3aed",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon name="icCheck" size={14} color="#ffffff" />
      </Box>
    );
  }
  return (
    <Box
      sx={{
        width: 22,
        height: 22,
        borderRadius: "50%",
        border: "1.5px solid rgba(255, 255, 255, 0.22)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <Icon name="icCheck" size={14} color="rgba(255, 255, 255, 0.22)" />
    </Box>
  );
}

function renderContent(text: string) {
  return text.split("\n").map((line, i) => {
    const trimmed = line.trim();
    if (!trimmed) return <Box key={i} sx={{ height: "8px" }} />;

    const isSectionTitle =
      /^제\d+조/.test(trimmed) ||
      /^第\d+[条條]/.test(trimmed) ||
      /^Article \d+/.test(trimmed) ||
      (/^\d+\.\s/.test(trimmed) && !trimmed.startsWith("- "));
    const isSubTitle = [
      "수집 항목",
      "이용 목적",
      "보유 및 이용 기간",
      "収集項目",
      "利用目的",
      "保有及び利用期間",
      "Information Collected",
      "Purposes of Use",
      "Retention and Use Period",
      "蒐集項目",
      "使用目的",
      "保存及使用期間",
    ].includes(trimmed);

    return (
      <Typography
        key={i}
        sx={{
          fontSize: isSectionTitle ? 12.5 : isSubTitle ? 12 : 12,
          fontWeight: isSectionTitle || isSubTitle ? 600 : 400,
          lineHeight: 1.6,
          color: isSectionTitle
            ? "rgba(255, 255, 255, 0.85)"
            : isSubTitle
              ? "rgba(255, 255, 255, 0.78)"
              : "rgba(255, 255, 255, 0.55)",
          mt: isSectionTitle ? "2px" : 0,
        }}
      >
        {trimmed}
      </Typography>
    );
  });
}
